"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import { X, ArrowLeft, MapPin, Monitor, HelpCircle } from "lucide-react";
import { TermsDialog, useTermsDialog } from "./terms-dialog";
import { BundleMembershipHelpPopup } from "./bundle-membership-help-popup";
import type { TermsSettings, CustomTerms } from "./utils/types";

type CheckoutPanelContextValue = {
  onClose?: () => void;
  isEmbedMode?: boolean;
  /** Measured height of the sticky panel header (px). 0 when no header is rendered. */
  headerHeight: number;
  /** Internal: called by CheckoutPanel.Header to publish its measured height. */
  setHeaderHeight: (height: number) => void;
};

const CheckoutPanelContext = React.createContext<CheckoutPanelContextValue>({
  headerHeight: 0,
  setHeaderHeight: () => {},
});

function useCheckoutPanel() {
  return React.useContext(CheckoutPanelContext);
}

/**
 * Optional header actions injected by an ancestor (e.g. CheckoutOrchestrator)
 * so CheckoutPanel.Header can render a Back affordance without every flow
 * threading the handler. Empty by default → no Back button, so existing
 * callers (widget, webcomponents) are unaffected.
 */
type CheckoutHeaderActions = { onBack?: () => void };
const CheckoutHeaderActionsContext =
  React.createContext<CheckoutHeaderActions>({});

function CheckoutHeaderActionsProvider({
  value,
  children,
}: {
  value: CheckoutHeaderActions;
  children: React.ReactNode;
}) {
  return (
    <CheckoutHeaderActionsContext.Provider value={value}>
      {children}
    </CheckoutHeaderActionsContext.Provider>
  );
}

type CheckoutPanelProps = {
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
  // New props (matching @obby/components API)
  title?: string;
  thumbnail?: string;
  currency?: string;
  isOnline?: boolean;
  availability?: number;
  hideDisplayAvailableTickets?: boolean;
  cancellationPolicyUrl?: string;
  /** Terms settings for dialog display */
  termsSettings?: TermsSettings;
  /** Custom terms for dialog display */
  customTerms?: CustomTerms;
  /** Timezone for date formatting */
  timezone?: string;
  /** Callback for webcomponents to open terms dialog in external portal */
  onOpenTermsDialog?: (data: {
    termsSettings?: TermsSettings;
    customTerms?: CustomTerms;
    timezone?: string;
  }) => void;
  source?: string;
  /** When true, panel doesn't use min-h-screen (for iframe embeds) */
  isEmbedMode?: boolean;
  /** When true, shows the bundle/membership helper. Widget-only, basket-enabled schools. */
  showBundleMembershipHelp?: boolean;
};

function CheckoutPanel({
  children,
  className,
  onClose,
  title,
  thumbnail,
  currency,
  isOnline,
  availability,
  hideDisplayAvailableTickets = false,
  cancellationPolicyUrl,
  termsSettings,
  customTerms,
  timezone,
  onOpenTermsDialog,
  source,
  isEmbedMode = false,
  showBundleMembershipHelp = false,
}: CheckoutPanelProps) {
  const termsDialog = useTermsDialog();
  const [isBundleHelpOpen, setBundleHelpOpen] = React.useState(false);
  const [headerHeight, setHeaderHeight] = React.useState(0);
  const panelContextValue = React.useMemo(
    () => ({ onClose, isEmbedMode, headerHeight, setHeaderHeight }),
    [onClose, isEmbedMode, headerHeight],
  );
  // Check if we have terms settings to show dialog
  const hasTermsSettings = termsSettings?.cancellationPolicy || termsSettings?.reschedulePolicy || termsSettings?.termsAndConditions;

  // If title/thumbnail props are provided, render a simplified layout
  // with sidebar containing the course info card
  const hasInfoProps = title !== undefined;

  if (hasInfoProps) {
    return (
      <CheckoutPanelContext.Provider value={panelContextValue}>
        <div className={cn(!isEmbedMode && "min-h-screen", "bg-background", className)}>
          <div className="container mx-auto px-1 sm:px-4 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Main content area */}
              <div className="lg:col-span-8">{children}</div>

              {/* Sidebar with course info - hidden on mobile/tablet */}
              <aside className="hidden lg:block lg:col-span-4">
                <div className="sticky top-4">
                  <div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
                    {/* Thumbnail - shorter on mobile/tablet, taller on desktop sidebar */}
                    {thumbnail && (
                      <div className="aspect-[3/1] lg:aspect-video overflow-hidden">
                        <img
                          src={thumbnail}
                          alt={title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    {/* Course info */}
                    <div className="p-4">
                      <h3 className="font-bold text-foreground uppercase tracking-wide text-lg">
                        {title}
                      </h3>

                      <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                        {isOnline !== undefined && (
                          <span className="flex items-center gap-1">
                            {isOnline ? (
                              <>
                                <Monitor className="w-4 h-4" />
                                Online
                              </>
                            ) : (
                              <>
                                <MapPin className="w-4 h-4" />
                                In-person
                              </>
                            )}
                          </span>
                        )}
                        {availability !== undefined && availability > 0 && (
                          <span className="text-primary font-medium">
                            {hideDisplayAvailableTickets ? "Spots available" : `${availability} spots left`}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Links section */}
                    {(cancellationPolicyUrl || showBundleMembershipHelp) && (
                      <div className="px-4 py-3 border-t border-border/50 space-y-2">
                        {cancellationPolicyUrl && (
                          hasTermsSettings ? (
                            <button
                              type="button"
                              onPointerDown={(e) => {
                                // Stop propagation to prevent DismissableLayer from detecting this as an outside click
                                // Radix uses pointerdown, not click, for outside click detection
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Use external portal callback (for webcomponents) or built-in dialog
                                if (onOpenTermsDialog) {
                                  onOpenTermsDialog({ termsSettings, customTerms, timezone });
                                } else {
                                  termsDialog.open();
                                }
                              }}
                              className="text-sm text-primary hover:text-primary/80 font-medium block text-left"
                            >
                              Cancellation Policy
                            </button>
                          ) : (
                            <a
                              href={cancellationPolicyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:text-primary/80 font-medium block"
                            >
                              Cancellation Policy
                            </a>
                          )
                        )}
                        {showBundleMembershipHelp && (
                          <button
                            type="button"
                            onClick={() => setBundleHelpOpen(true)}
                            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 text-left"
                          >
                            <HelpCircle className="w-4 h-4" />
                            Have a bundle or membership? Learn more
                          </button>
                        )}
                      </div>
                    )}

                    {/* Terms Dialog */}
                    <TermsDialog
                      isOpen={termsDialog.isOpen}
                      onClose={termsDialog.close}
                      settings={termsSettings}
                      customTerms={customTerms}
                      timezone={timezone}
                    />
                    {showBundleMembershipHelp && (
                      <BundleMembershipHelpPopup
                        open={isBundleHelpOpen}
                        onOpenChange={setBundleHelpOpen}
                      />
                    )}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </CheckoutPanelContext.Provider>
    );
  }

  // Original compound component layout
  return (
    <CheckoutPanelContext.Provider value={panelContextValue}>
      <div className={cn(!isEmbedMode && "min-h-screen", "bg-background", className)}>{children}</div>
    </CheckoutPanelContext.Provider>
  );
}

type CheckoutPanelHeaderProps = {
  className?: string;
  title?: string;
  subtitle?: string;
  onClose?: () => void;
  /**
   * Renders a Back button left of the title. May also be supplied via
   * CheckoutHeaderActionsProvider (the prop wins). Omit for no Back button.
   */
  onBack?: () => void;
};

function CheckoutPanelHeader({
  className,
  title,
  subtitle,
  onClose: propOnClose,
  onBack: propOnBack,
}: CheckoutPanelHeaderProps) {
  const { onClose: contextOnClose, isEmbedMode, setHeaderHeight } = useCheckoutPanel();
  const { onBack: contextOnBack } = React.useContext(CheckoutHeaderActionsContext);
  const onClose = propOnClose ?? contextOnClose;
  const onBack = propOnBack ?? contextOnBack;
  const headerRef = React.useRef<HTMLElement>(null);

  // Publish header height to context so sticky descendants (e.g. date toolbar)
  // can offset themselves correctly when this header is sticky.
  React.useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    if (isEmbedMode) {
      setHeaderHeight(0);
      return;
    }
    setHeaderHeight(el.getBoundingClientRect().height);
    const obs = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setHeaderHeight(entry.contentRect.height);
    });
    obs.observe(el);
    return () => {
      obs.disconnect();
      setHeaderHeight(0);
    };
  }, [isEmbedMode, setHeaderHeight]);

  return (
    <header
      ref={headerRef}
      className={cn(
        "z-50 bg-card border-b border-border",
        // In embed mode: relative positioning, otherwise sticky at top
        !isEmbedMode && "sticky top-0",
        className,
      )}
    >
      <div className="container mx-auto px-1 sm:px-4 py-3 flex items-center justify-between">
        {/* When onBack is provided (opt-in, youmo only) the title sits next to a
            Back chevron; otherwise this renders exactly as before so existing
            consumers (widget, webcomponents) are byte-identical. */}
        {onBack ? (
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <button
              type="button"
              onClick={onBack}
              className="p-2 -ml-1 hover:bg-muted rounded-full transition-colors shrink-0"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div className="min-w-0">
              {title && (
                <h1 className="text-lg font-bold text-foreground">{title}</h1>
              )}
              {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
            </div>
          </div>
        ) : (
          <div className="flex-1">
            {title && (
              <h1 className="text-lg font-bold text-foreground">{title}</h1>
            )}
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
        )}
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-muted-foreground" />
          </button>
        )}
      </div>
    </header>
  );
}

type CheckoutPanelContentProps = {
  children: React.ReactNode;
  className?: string;
  /** When true, no bottom padding for fixed footer (embed mode) */
  isEmbedMode?: boolean;
};

function CheckoutPanelContent({
  children,
  className,
  isEmbedMode = true,
}: CheckoutPanelContentProps) {
  return (
    <main
      data-checkout-content
      className={cn(
        "container mx-auto px-2 sm:px-4 py-4 sm:py-6",
        // Only add bottom padding for fixed footer when not in embed mode
        !isEmbedMode && "pb-32",
        className
      )}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">{children}</div>
    </main>
  );
}

type CheckoutPanelMainProps = {
  children: React.ReactNode;
  className?: string;
};

function CheckoutPanelMain({ children, className }: CheckoutPanelMainProps) {
  return <div className={cn("lg:col-span-8", className)}>{children}</div>;
}

type CheckoutPanelSidebarProps = {
  children: React.ReactNode;
  className?: string;
};

function CheckoutPanelSidebar({
  children,
  className,
}: CheckoutPanelSidebarProps) {
  const { isEmbedMode } = useCheckoutPanel();
  return (
    <aside className={cn("hidden lg:block lg:col-span-4", className)}>
      <div className={cn(!isEmbedMode && "sticky top-20")}>{children}</div>
    </aside>
  );
}

type CheckoutPanelFooterProps = {
  children: React.ReactNode;
  className?: string;
};

function CheckoutPanelFooter({
  children,
  className,
}: CheckoutPanelFooterProps) {
  return (
    <footer
      className={cn(
        "fixed bottom-0 left-0 right-0 bg-card border-t border-border py-4 px-4",
        "shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]",
        className,
      )}
    >
      <div className="container mx-auto flex items-center justify-between">
        {children}
      </div>
    </footer>
  );
}

CheckoutPanel.Header = CheckoutPanelHeader;
CheckoutPanel.Content = CheckoutPanelContent;
CheckoutPanel.Main = CheckoutPanelMain;
CheckoutPanel.Sidebar = CheckoutPanelSidebar;
CheckoutPanel.Footer = CheckoutPanelFooter;

export { CheckoutPanel, useCheckoutPanel, CheckoutHeaderActionsProvider };
export type { CheckoutPanelProps };
